import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getApiErrorUserMessage, readApiError } from "_shared/_errors/api-error";
import { getApiErrorData, getApiErrorStatus } from "_shared/_errors/api-error-message";
import { api } from "_shared/_stores/api";
import Guid from "_shared/guids/guid";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import { EntityAgreementJSON } from "legal-entities/agreements/_schema/entity-agreement-schema";
import AcceptEntityUserInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/accept-entity-user-invitation-api-command";
import InviteEntityOfficerAPIRequest from "legal-entities/invitations/officer-invitations/_api/commands/invite-entity-officer-api-request";
import EntityOfficerInvitation, {
  EntityOfficerInvitationNotOpenError,
  EntityOfficerInvitationNotFoundError,
  UnauthorizedEntityOfficerInvitationAccessError,
} from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { EntityOfficerInvitationJSON } from "legal-entities/invitations/officer-invitations/_schema/entity-officer-invitation-schema";
import { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import Session from "users/__session/session";

type InviteEntityOfficerArgs = {
  invitation: EntityOfficerInvitation;
  operationId: Guid;
};

export enum OfficerSetupOperationStatus {
  Pending = "Pending",
  Completed = "Completed",
}

export enum OfficerSetupStageStatus {
  Pending = "Pending",
  Started = "Started",
  Completed = "Completed",
  NotApplicable = "NotApplicable",
}

export enum OfficerSetupPendingStage {
  DeliveryUnknown = "DeliveryUnknown",
}

export type OfficerSetupStatus = {
  operationId: string;
  status: OfficerSetupOperationStatus;
  stages: {
    account: OfficerSetupStageStatus;
    permission: OfficerSetupStageStatus;
    marketplace: OfficerSetupStageStatus;
    invitation: OfficerSetupStageStatus;
    delivery: OfficerSetupStageStatus;
  };
  invitationId?: string;
};

type OfficerSetupPendingResponse = {
  operationId: string;
  status: OfficerSetupOperationStatus.Pending;
  stage: OfficerSetupPendingStage.DeliveryUnknown;
  traceId?: string;
};

type OfficerInvitationIdArgs = {
  id: Guid;
};

type AcceptOfficerInvitationArgs = {
  invitation: EntityOfficerInvitation;
  session: Session;
};

type CancelOfficerInvitationArgs = {
  invitation: EntityOfficerInvitation;
};

// The superuser dashboard's list of still-open entity officer invitations. Sending and
// cancelling both change what belongs on it.
const openOfficerInvitationsTag = { type: "EntityOfficerInvitation" as const, id: "OPEN_LIST" };

const jsonHeaders = {
  "Content-Type": "application/json",
};

export const entityOfficerInvitationApi = api.injectEndpoints({
  endpoints: (build) => ({
    inviteEntityOfficer: build.mutation<EntityOfficerInvitation, InviteEntityOfficerArgs>({
      async queryFn({ invitation, operationId }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: "super-user/invite-entity-officer",
          method: "POST",
          body: JSON.stringify(new InviteEntityOfficerAPIRequest(invitation, operationId)),
          headers: jsonHeaders,
        });

        if (response.error) {
          const status = getApiErrorStatus(response.error);
          if (typeof status === "string") {
            return {
              error: toQueryError(new OfficerSetupPendingError({
                operationId: operationId.value,
                status: OfficerSetupOperationStatus.Pending,
                stage: OfficerSetupPendingStage.DeliveryUnknown,
              })),
            };
          }
          return {
            error: toQueryError(new EntityOfficerInvitationServiceError("inviteEntityOfficer", response.error, invitation)),
          };
        }

        if (isOfficerSetupPendingResponse(response.data)) {
          return { error: toQueryError(new OfficerSetupPendingError(response.data)) };
        }

        return {
          data: EntityOfficerInvitation.create({
            params: {
              ...invitation,
              id: new Guid(response.data as string),
            },
          }),
        };
      },
      invalidatesTags: [openOfficerInvitationsTag],
    }),
    getOfficerSetupStatus: build.query<OfficerSetupStatus, Guid>({
      async queryFn(operationId, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery(`legal-entities/officer-setups/${operationId.value}`);
        if (response.error) {
          if (typeof getApiErrorStatus(response.error) === "string") {
            return { error: toQueryError(new OfficerSetupPendingError({
              operationId: operationId.value,
              status: OfficerSetupOperationStatus.Pending,
              stage: OfficerSetupPendingStage.DeliveryUnknown,
            })) };
          }
          return {
            error: toQueryError(new EntityOfficerInvitationServiceError("getOfficerSetupStatus", response.error)),
          };
        }
        return { data: response.data as OfficerSetupStatus };
      },
    }),
    getAllOpenOfficerInvitations: build.query<EntityOfficerInvitation[], void>({
      async queryFn(_args, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery("legal-entities/officer-invitations");

        if (response.error) {
          return {
            error: toQueryError(new EntityOfficerInvitationServiceError("getAllOpenOfficerInvitations", response.error)),
          };
        }

        return {
          data: EntityOfficerInvitation.fromJSONArray(
            (response.data as { invitations?: EntityOfficerInvitationJSON[] }).invitations ?? [],
          ),
        };
      },
      providesTags: [openOfficerInvitationsTag],
    }),
    getOfficerInvitationById: build.query<EntityOfficerInvitation, OfficerInvitationIdArgs>({
      async queryFn({ id }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery(`legal-entities/officer-invitations/${id.value}`);

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new EntityOfficerInvitationNotFoundError(id)) };
          }

          return {
            error: toQueryError(new EntityOfficerInvitationServiceError("getOfficerInvitationById", response.error)),
          };
        }

        return { data: EntityOfficerInvitation.fromJSON(response.data as EntityOfficerInvitationJSON) };
      },
    }),
    acceptOfficerInvitation: build.mutation<EntityAgreement, AcceptOfficerInvitationArgs>({
      async queryFn({ invitation, session }, _queryApi, _extraOptions, baseQuery) {
        if (!invitation.id) {
          return { error: toQueryError(new Error("Invitation ID is required to accept an invitation.")) };
        }
        if (!invitation.registrationInfo?.password) {
          return { error: toQueryError(new Error("Password is required to accept an invitation.")) };
        }

        const response = await baseQuery({
          url: `legal-entities/officer-invitations/${invitation.id.value}/accept`,
          method: "POST",
          body: JSON.stringify(
            new AcceptEntityUserInvitationAPIRequest(
              invitation.registrationInfo.password,
              invitation.registrationInfo.name,
            ),
          ),
          headers: jsonHeaders,
        });

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.Conflict) {
            return { error: toQueryError(new EntityOfficerInvitationNotOpenError(conflictMessage(response.error))) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.BadRequest) {
            return { error: toQueryError(new InvalidRegistrationInfoError(getApiErrorData(response.error))) };
          }

          return {
            error: toQueryError(new EntityOfficerInvitationServiceError("acceptOfficerInvitation", response.error)),
          };
        }

        // Accepting an officer invitation is a login context: the response carries the shared auth
        // payload at the top level, alongside the agreement the new officer still has to accept.
        const data = response.data as { entityAgreement: unknown };
        session.initializeFromAuthPayload(response.data);

        return { data: EntityAgreement.fromJSON(data.entityAgreement as EntityAgreementJSON) };
      },
    }),
    cancelEntityOfficerInvitation: build.mutation<void, CancelOfficerInvitationArgs>({
      async queryFn({ invitation }, _queryApi, _extraOptions, baseQuery) {
        if (!invitation.id) {
          return { error: toQueryError(new Error("Cannot cancel an invitation that has not been saved.")) };
        }

        const response = await baseQuery({
          url: `legal-entities/officer-invitations/${invitation.id.value}/cancel`,
          method: "POST",
          body: {},
        });

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new EntityOfficerInvitationNotFoundError(invitation.id)) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.Forbidden) {
            return { error: toQueryError(new UnauthorizedEntityOfficerInvitationAccessError()) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.Conflict) {
            return { error: toQueryError(new EntityOfficerInvitationNotOpenError(conflictMessage(response.error))) };
          }

          return {
            error: toQueryError(
              new EntityOfficerInvitationServiceError("cancelEntityOfficerInvitation", response.error, invitation),
            ),
          };
        }

        return { data: null as unknown as void };
      },
      invalidatesTags: [openOfficerInvitationsTag],
    }),
  }),
});

export class EntityOfficerInvitationServiceError extends Error {
  invitation?: EntityOfficerInvitation;
  method: string;
  error: unknown;

  constructor(method: string, error: unknown, invitation?: EntityOfficerInvitation) {
    super(`Error inviting entity officer: ${error instanceof Error ? error.message : String(error)}`);
    this.method = method;
    this.error = error;
    this.invitation = invitation;
  }
}

export class OfficerSetupPendingError extends Error {
  readonly operationId: Guid;
  readonly traceId?: string;

  constructor(response: OfficerSetupPendingResponse) {
    super("The officer invitation delivery result is still pending.");
    this.operationId = new Guid(response.operationId);
    this.traceId = response.traceId;
  }
}

function isOfficerSetupPendingResponse(data: unknown): data is OfficerSetupPendingResponse {
  if (!data || typeof data !== "object") return false;
  const candidate = data as Partial<OfficerSetupPendingResponse>;
  return candidate.status === OfficerSetupOperationStatus.Pending &&
    candidate.stage === OfficerSetupPendingStage.DeliveryUnknown &&
    typeof candidate.operationId === "string" &&
    (candidate.traceId === undefined || typeof candidate.traceId === "string");
}

function toQueryError(error: Error): FetchBaseQueryError {
  return error as unknown as FetchBaseQueryError;
}

function conflictMessage(error: unknown): string | undefined {
  const result = readApiError(error);
  return result.trusted ? getApiErrorUserMessage(result) : undefined;
}

export const {
  useInviteEntityOfficerMutation,
  useLazyGetOfficerSetupStatusQuery,
  useGetAllOpenOfficerInvitationsQuery,
  useLazyGetOfficerInvitationByIdQuery,
  useGetOfficerInvitationByIdQuery,
  useAcceptOfficerInvitationMutation,
  useCancelEntityOfficerInvitationMutation,
} = entityOfficerInvitationApi;
